
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock, User, Search, Calendar } from "lucide-react";
import { blogPosts } from "../data/blogdata";

function Blog() {
  const posts = blogPosts;
  const loading = false;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredPosts = useMemo(() => {
    let filtered = posts;

    if (selectedCategory !== "all") {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title?.toLowerCase().includes(s) ||
          p.excerpt?.toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [posts, searchTerm, selectedCategory]);

  const categories = ["all", ...new Set(posts.map((p) => p.category))];

  // formatDate et le return (JSX) restent exactement comme avant
 const formatDate = (timestamp) =>
    new Date(timestamp * 1000).toLocaleDateString("fr-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <div className="min-h-screen w-full bg-slate-950 text-white">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5 px-4 py-24 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/40 via-slate-950 to-slate-950" />

        <div className="relative mx-auto max-w-7xl text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Blog SERVICE <span className="text-blue-500">IPTV</span>
          </h1>

          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-400 sm:text-xl">
            Guides, conseils et actualités sur le meilleur streaming IPTV au
            Canada et en France
          </p>
        </div>
      </section>

      {/* MAIN */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[280px_1fr]">
            {/* SIDEBAR */}
            <aside className="space-y-8">
              {/* Recherche */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-xl backdrop-blur-xl">
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 px-4 py-3 focus-within:border-blue-500/50">
                  <Search size={20} className="flex-shrink-0 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Rechercher un article..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-gray-500"
                  />
                </div>
              </div>

              {/* Catégories */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-xl backdrop-blur-xl">
                <h3 className="mb-5 text-lg font-bold text-white">Catégories</h3>

                <div className="space-y-2">
                  {categories.map((cat) => {
                    const count =
                      cat === "all"
                        ? posts.length
                        : posts.filter((p) => p.category === cat).length;
                    const active = selectedCategory === cat;

                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setSelectedCategory(cat)}
                        className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm font-medium transition-all duration-200 ${
                          active
                            ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                            : "text-gray-400 hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        <span>{cat === "all" ? "Tous les articles" : cat}</span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            active
                              ? "bg-white/20 text-white"
                              : "bg-white/10 text-gray-400"
                          }`}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Populaires */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-xl backdrop-blur-xl">
                <h3 className="mb-5 text-lg font-bold text-white">Populaires</h3>

                <div className="space-y-5">
                  {posts.slice(0, 5).map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="group block border-b border-white/5 pb-4 last:border-0 last:pb-0"
                    >
                      <div className="mb-2 text-xs text-gray-500">
                        {post.category}
                      </div>
                      <p className="text-sm font-medium leading-5 text-gray-300 transition-colors group-hover:text-blue-400">
                        {post.title}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>

            {/* ARTICLES */}
            <main>
              {loading ? (
                <div className="flex min-h-[400px] items-center justify-center">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/10 border-t-blue-500" />
                </div>
              ) : filteredPosts.length > 0 ? (
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
                  {filteredPosts.map((post) => (
                    <article
                      key={post.id}
                      className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-xl backdrop-blur-xl transition-all duration-300 hover:-translate-y-2 hover:border-blue-500/30 hover:shadow-2xl"
                    >
                      {post.image && (
                        <Link
                          to={`/blog/${post.slug}`}
                          className="relative block overflow-hidden"
                        >
                          <img
                            src={post.image}
                            alt={post.title}
                            loading="lazy"
                            className="h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <span className="absolute left-4 top-4 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white shadow-lg">
                            {post.category}
                          </span>
                        </Link>
                      )}

                      <div className="flex flex-1 flex-col p-6">
                        <h2 className="text-xl font-bold leading-7">
                          <Link
                            to={`/blog/${post.slug}`}
                            className="text-white transition-colors hover:text-blue-400"
                          >
                            {post.title}
                          </Link>
                        </h2>

                        <p className="mt-3 line-clamp-3 text-sm leading-6 text-gray-400">
                          {post.excerpt}
                        </p>

                        <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1.5">
                            <User size={14} />
                            {post.author}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Calendar size={14} />
                            {formatDate(post.createdAt)}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock size={14} />
                            {post.readingTime} min
                          </span>
                        </div>

                        <div className="mt-auto pt-6">
                          <Link
                            to={`/blog/${post.slug}`}
                            className="inline-flex items-center text-sm font-semibold text-blue-400 transition-all duration-200 hover:gap-3 hover:text-blue-300"
                          >
                            Lire la suite
                            <span className="ml-2">→</span>
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="flex min-h-[400px] items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
                  <div>
                    <Search size={48} className="mx-auto mb-4 text-gray-600" />
                    <p className="text-lg font-medium text-gray-300">
                      Aucun article trouvé.
                    </p>
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm("")}
                        className="mt-4 text-sm font-medium text-blue-400 hover:text-blue-300"
                      >
                        Effacer la recherche
                      </button>
                    )}
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Blog;